import { useEffect } from 'react'
import { googleFontsHref } from '../render/fonts'

type Weights = 'regular' | 'full'

/*
  What has been asked for, and at how many weights. A stylesheet is never
  removed once added: the faces it carries are on screen somewhere, and taking
  it away would unstyle them.

  Two levels rather than one, because a family first seen as a row in a list
  needs only its regular, and the same family chosen for the page then needs its
  bold as well. `full` covers `regular`, never the other way round.
*/
const loaded = new Map<string, Weights>()

const missing = (family: string, weights: Weights) => {
  const have = loaded.get(family)
  if (have === 'full') return false
  return have !== weights
}

/*
  How many families go into one request. The API takes any number, but a URL
  does not: asking for all 1818 at once is 60 kB of query string, well past what
  Google will answer. Chunking also means the families already loaded keep their
  stylesheet while new ones get their own.
*/
const CHUNK = 60

const pending: Record<Weights, string[]> = { regular: [], full: [] }
let flushHandle: ReturnType<typeof setTimeout> | null = null

function append(families: string[], weights: Weights) {
  const href = googleFontsHref(families, weights)
  if (!href) return

  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  link.dataset.mpFonts = 'true'
  document.head.appendChild(link)
}

function flush() {
  flushHandle = null
  ;(['regular', 'full'] as Weights[]).forEach(weights => {
    const families = pending[weights]
    pending[weights] = []
    for (let index = 0; index < families.length; index += CHUNK) {
      append(families.slice(index, index + CHUNK), weights)
    }
  })
}

/**
 * Asks for a set of families once, batching whatever else was asked for in the
 * same tick into the same request.
 *
 * A scrolling list of specimens asks one family at a time, dozens per second;
 * without the buffer that is dozens of round trips for what fits in one.
 */
export function ensureFonts(
  families: readonly string[],
  weights: Weights = 'full'
) {
  const fresh = families.filter(family => family && missing(family, weights))
  if (fresh.length === 0) return

  fresh.forEach(family => loaded.set(family, weights))
  pending[weights].push(...fresh)

  if (flushHandle) return
  flushHandle = setTimeout(flush, 80)
}

/** Loads a fixed set of families for as long as the page is open. */
export function useGoogleFonts(families: readonly string[]) {
  // The joined names are the identity of the set: same families, same effect.
  const key = [...families].sort().join('|')

  useEffect(() => {
    ensureFonts(key.split('|'))
  }, [key])
}

/*
  One observer for every specimen in a list of 1818, rather than one each. A row
  asks for its face when it is first scrolled into view and never again, which
  is what keeps the whole catalogue browsable without fetching a font file for
  every family in it.
*/
let observer: IntersectionObserver | null = null
const families = new WeakMap<Element, string>()

function specimenObserver() {
  if (observer) return observer
  observer = new IntersectionObserver(
    entries => {
      const seen = entries
        .filter(entry => entry.isIntersecting)
        .map(entry => {
          observer?.unobserve(entry.target)
          return families.get(entry.target) ?? ''
        })
      // A row shows a name, never a bold one.
      ensureFonts(seen, 'regular')
    },
    // A screen's worth of margin, so a face is on its way before it is read.
    { rootMargin: '200px' }
  )
  return observer
}

/** Loads one family the first time its row is scrolled into view. */
export function observeSpecimen(element: Element | null, family: string) {
  if (!element) return undefined
  families.set(element, family)
  const active = specimenObserver()
  active.observe(element)
  return () => active.unobserve(element)
}
